import React, { useState, useEffect, useCallback } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppMode } from '../../contexts/AppModeContext'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { CommandPalette } from './CommandPalette'
import { NotificationToast } from './NotificationToast'
import { WhatsAppDisconnectedBanner } from './WhatsAppDisconnectedBanner'
import { GlobalQueueAlerts } from './GlobalQueueAlerts'
import { GlobalWhisperInvites } from './GlobalWhisperInvites'
import { TodoNotebook } from '../todo/TodoNotebook'
import { CallsDock } from '../calls/CallsDock'
import { CrmAttentionDock } from '../crm/CrmAttentionDock'
import { CrmClockStrip } from '../crm/CrmClockStrip'

interface LayoutProps {
  children: React.ReactNode
}

/* ─── Dark mode helper ─────────────────────────────────────────────────── */

function getInitialDarkMode(): boolean {
  try {
    const stored = localStorage.getItem('wq_dark_mode')
    if (stored !== null) return stored === 'true'
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

/* ─── Layout Component ─────────────────────────────────────────────────── */

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { mode } = useAppMode()
  const [isDark, setIsDark] = useState(getInitialDarkMode)
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false)
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('wq_nav_collapsed') === 'true' } catch { return false }
  })
  const toggleNav = useCallback(() => setNavCollapsed((v) => {
    const next = !v
    try { localStorage.setItem('wq_nav_collapsed', String(next)) } catch { /* ignore */ }
    return next
  }), [])

  // Apply dark class to <html>
  useEffect(() => {
    const root = document.documentElement
    if (isDark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    localStorage.setItem('wq_dark_mode', String(isDark))
  }, [isDark])

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
  const location = useLocation()
      // Cmd+K or Ctrl+K → Command Palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsCommandPaletteOpen((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const toggleDark = useCallback(() => setIsDark((prev) => !prev), [])

  // In dev mode, no Support/CRM page (and therefore none of the notification / ring
  // components rendered below) should be mounted — send everything to the blank
  // dev workspace, which has its own minimal shell.
  if (mode === 'dev') return <Navigate to="/dev" replace />

  return (
    <div className="flex h-screen bg-surface text-txt-primary transition-colors duration-200 overflow-hidden">
      {/* Sidebar (collapses to the left edge) */}
      <Sidebar
        onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
        collapsed={navCollapsed}
        onToggleCollapse={toggleNav}
      />

      {/* Re-open tab — a chevron on the screen edge when the nav is collapsed */}
      {navCollapsed && (
        <button
          onClick={toggleNav}
          title="Show navigation"
          // Same vertical position (top-4) as the nav's own collapse button, so
          // the control doesn't jump to mid-screen when the nav is hidden.
          className="fixed left-1 top-4 z-40 flex items-center justify-center w-6 h-6 rounded-md bg-surface border border-border text-txt-secondary hover:text-brand-600 hover:bg-surface-hover shadow-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </button>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Global top header bar (Copilot, Sound, Settings, Profile) */}
        <TopBar isDark={isDark} onToggleDark={toggleDark} />
        {/* Lead-time wall clocks (Lee, 23 Sep 2026) — one clock per distinct
            current time across all leads, grouped where zones overlap.
            CRM ROUTES ONLY (the gate lives inside the component). */}
        <CrmClockStrip />
        {/* Critical integration alerts — only renders when state != connected */}
        <WhatsAppDisconnectedBanner />
        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-surface-secondary">
          {children}
        </main>
      </div>

      {/* Command Palette overlay */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />

      {/* Notification toasts */}
      <NotificationToast />

      {/* Global queue alert ring-loop — runs for every authenticated agent
          on every page, so new chats are heard regardless of where the
          agent is in the app and continue ringing until someone picks up. */}
      <GlobalQueueAlerts />

      {/* Whisper invitations — top banner + tune when invited to whisper. */}
      <GlobalWhisperInvites />

      {/* Personal TO-DO notebook — a private, per-agent list docked to the right
          edge on every authenticated screen. Standalone (own localStorage,
          portals to body); doesn't touch any other feature. */}
      {/* The three docks hide on My Day — the page IS those panels (Lee, 26 Oct 2026). */}
      {!location.pathname.startsWith('/crm/my-day') && <TodoNotebook />}

      {/* CALLS dock — the day's calls/meetings/Google events, right-edge tab under
          the notebook. SUPPORT MODE ONLY (renders null in Dev Mode). */}
      {!location.pathname.startsWith('/crm/my-day') && <CallsDock />}

      {/* NEEDS YOUR ATTENTION dock (Lee, 22 Sep 2026) — activity-down / churn /
          alert feed, floating bottom-right. CRM ROUTES ONLY (renders null
          elsewhere — the gate lives inside the component). Standalone. */}
      {!location.pathname.startsWith('/crm/my-day') && <CrmAttentionDock />}
    </div>
  )
}
